<template>
	<Popper
		v-click-outside="closePopover"
		class="flex"
		data-test="session-assignee-manager"
		:isVisible="popoverVisible"
		placement="left"
		fixed
		@keyup.escape="closePopover"
	>
		<div v-if="assigneeUsers?.length" class="flex flex-wrap gap-8 mb-8">
			<BaseButton
				v-for="assignee in assigneeUsers"
				:key="assignee.id"
				data-test="applied-assignee"
				class="text-gray-400 rounded-full button-dashed border-solid"
				:disabled="$store.isExtension || !canUserEditSession"
				@click="goToFilteredSessionsList(assignee)"
			>
				<BaseUserAvatar :url="assignee.avatar" :name="assignee.name" :size="16" class="-ml-4" />
				<span class="text-gray-300 truncate">{{ assignee.name }}</span>
			</BaseButton>
		</div>
		<BaseButton
			v-if="canUserEditSession"
			data-test="assignee-trigger-button"
			:class="{
				'text-gray-300 button-dashed rounded-full': true,
				active: popoverVisible,
				'cursor-not-allowed text-gray-400': !isAssigneesFeatureEnabled,
			}"
			:disabled="!isAssigneesFeatureEnabled"
			@click="togglePopover"
		>
			<template #icon><BaseIcon name="mono/circle" /></template>

			<BaseTooltip v-if="!isAssigneesFeatureEnabled" :text="'Upgrade to team plan to add assignees'">
				Add assignees
			</BaseTooltip>
			<span v-else>Add {{ assigneeUsers.length ? 'or remove' : '' }} assignees</span>
		</BaseButton>
		<template #popup>
			<BaseSearchablePopover
				v-if="popoverVisible"
				v-model="search"
				searchPlaceholder="Assign to..."
				data-test="assignees-popover"
			>
				<menu data-test="assignees-menu" class="m-0 p-0 list-none mt-8">
					<li v-if="!search">
						<button
							class="popover-link"
							:class="{'text-gray-100': !assigneeUsers.length}"
							data-test="unassigned-button"
							@click="unassign"
						>
							<UserListItem>
								<BaseIcon name="mono/circle" class="w-20 h-20" />
								<span>Unassigned</span>
								<template #aside>
									<BaseIcon
										v-if="!assigneeUsers.length"
										name="mono/check"
										data-test="checkmark"
										class="mb-2 text-green-500"
									/>
								</template>
							</UserListItem>
						</button>
					</li>
					<template v-if="searchedUsers.length">
						<li v-for="user in searchedUsers" :key="user.id">
							<button
								class="popover-link"
								:class="{'text-gray-100': isUserAssigned(user.id)}"
								data-test="assignee-button"
								@click="onSelectUser(user)"
							>
								<UserListItem :user="user">
									<template #aside>
										<BaseIcon
											v-if="isUserAssigned(user.id)"
											name="mono/check"
											data-test="checkmark"
											class="mb-2 text-green-500"
										/>
									</template>
								</UserListItem>
							</button>
						</li>
					</template>
					<li v-else class="text-gray-400 text-sm pl-12 py-4">No users found</li>
				</menu>

				<template v-if="!$store.hasUserBugReporterRole && !$store.hasUserProductViewerRole" #footer>
					<InviteMemberInPopover location="Manage Session Assignees Popover" />
				</template>
			</BaseSearchablePopover>
		</template>
	</Popper>
</template>

<script>
import {defineAsyncComponent} from 'vue'
import {storeToRefs} from 'pinia'

import analytics from 'shared/utils/analytics'
import Popper from 'shared/components/Popper.vue'
import {useSessionStore} from 'shared/stores/session'
import {FEATURE_CODES} from 'shared/constants/featureCodes'

export default {
	name: 'SessionManageAssignees',
	components: {
		Popper,
		InviteMemberInPopover: defineAsyncComponent(() => import('shared/components/InviteMemberInPopover.vue')),
		UserListItem: defineAsyncComponent(() => import('shared/components/UserListItem.vue')),
	},
	setup() {
		const sessionStore = useSessionStore()
		const {session, canUserEditSession} = storeToRefs(sessionStore)
		return {session, canUserEditSession}
	},
	data() {
		return {
			popoverVisible: false,
			search: '',
			assigneeUsers: [],
		}
	},
	computed: {
		users() {
			return this.$store.users.filter(({role}) => role !== 'bug-reporter')
		},
		searchedUsers() {
			if (!this.search) return this.users
			return this.users.filter(user => {
				return user.name.toLowerCase().includes(this.search.toLowerCase())
			})
		},
		isAssigneesFeatureEnabled() {
			return this.$store.hasPlanFeature(FEATURE_CODES.INCLUDE_ASSIGNEES)
		},
	},
	watch: {
		'session.assignees': {
			handler(assignees) {
				if (!assignees?.length) return
				this.updateAssigneeUsers()
			},
			deep: true,
			immediate: true,
		},
	},
	methods: {
		goToFilteredSessionsList(assignee) {
			this.$routerHelpers.push(
				`/workspaces/${this.session.workspaceId}/collections/${this.session.collectionId}?filters=assignee:${assignee.email}`
			)
		},
		updateAssigneeUsers() {
			// assignees come in the format {user: {id: string, name: string, avatar: string}}[]
			this.assigneeUsers = this.session.assignees.map(({user}) => user)
			return
		},
		async onSelectUser(assignee) {
			if (this.isUserAssigned(assignee.id)) {
				this.assigneeUsers = this.assigneeUsers.filter(({id}) => id !== assignee.id)
				await this.saveSessionAssignees({
					assigneeIdsToAdd: [],
					assigneeIdsToRemove: [assignee.id],
				})
				return
			}
			this.assigneeUsers.push(assignee)
			await this.saveSessionAssignees({
				assigneeIdsToAdd: [assignee.id],
				assigneeIdsToRemove: [],
			})
		},
		async unassign() {
			if (!this.assigneeUsers.length) return
			const assigneeIdsToRemove = this.assigneeUsers.map(({id}) => id)
			this.assigneeUsers = []
			await this.saveSessionAssignees({
				assigneeIdsToAdd: [],
				assigneeIdsToRemove,
			})
		},
		isUserAssigned(userId) {
			return this.assigneeUsers.some(({id}) => id === userId)
		},
		async saveSessionAssignees({assigneeIdsToAdd, assigneeIdsToRemove}) {
			if (this.$store.isExtension) {
				const sessionAssignees = this.assigneeUsers.map(({name, id, avatar}) => ({
					name,
					user: {id, name, avatar},
				}))
				return document.dispatchEvent(new CustomEvent('sessionAssigneesChanged', {detail: {sessionAssignees}}))
			}
			return this.$api.post(`/sessions/assignees`, {
				assigneeIdsToAdd,
				assigneeIdsToRemove,
				sessionIds: [this.session.id],
			})
		},
		togglePopover() {
			if (!this.canUserEditSession) return
			if (this.popoverVisible) {
				this.closePopover()
			} else {
				this.popoverVisible = true
			}
			analytics.track('Session Assignee Button Clicked')
		},
		closePopover() {
			this.search = ''
			this.popoverVisible = false
		},
		openSettings() {
			analytics.track('Manage Members Button Clicked', {cta: 'Select Assignee Menu'})
			this.closePopover()
			this.$routerHelpers.navigateToWorkspaceSettings('members')
		},
	},
}
</script>
